import { FILE_SIZE, SUPPORTED_FORMATS } from 'constants/images';
import { useInfoPopup } from 'hooks';
import { isString } from 'lodash/fp';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { FieldError } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LogoUploadPlaceholder } from 'static';
import { invokeIfArgExists } from 'utils';

import ImageBox from './ImageBox';
import {
  ButtonsContainer,
  DeleteImageButton,
  HiddenInput,
  LabelWrapper,
  StyledDeleteIcon,
  TextButton,
  TipText,
} from './styled';

const validImageType = (imageFile) =>
  SUPPORTED_FORMATS.includes(imageFile.type);
const validImageSize = (imageFile) =>
  Math.round(imageFile.size / 1024) < FILE_SIZE;
const getUrl = invokeIfArgExists((file) =>
  isString(file) ? file : URL.createObjectURL(file),
);

interface ImageInputProps {
  register: any;
  unregister: any;
  setValue: (name: string, value: string) => void;
  name: string;
  placeholder?: string;
  label?: string;
  defaultValue?: string;
  error?: FieldError;
}

const ImageInput: FC<ImageInputProps> = ({
  register = () => {},
  unregister = () => {},
  setValue = () => {},
  name,
  placeholder = LogoUploadPlaceholder,
  label,
  defaultValue,
  error,
}) => {
  const { t } = useTranslation();
  const [image, setImage] = useState(defaultValue || null);
  const inputRef = useRef<HTMLInputElement>();
  const { showInfoPopup } = useInfoPopup();
  const [fileSizeTitle, fileSizeMessage] = t('errors.fileSizeCustom', {
    size: 5,
  }).split(':');

  useEffect(() => {
    register({ name });
    return () => unregister(name);
  }, [register, unregister, name]);

  useEffect(() => {
    setValue(name, image);
  }, [setValue, name, image]);

  const handleChange = (e) => {
    const imageFile = e.target.files[0];
    inputRef.current.value = null;

    if (!validImageSize(imageFile)) {
      showInfoPopup({
        title: fileSizeTitle,
        message: fileSizeMessage,
        type: 'warning',
        loop: true,
      });
    }

    if (!imageFile) return false;
    if (!validImageType(imageFile) || !validImageSize(imageFile)) return false;

    setImage(imageFile);
    return true;
  };

  const handleDelete = () => {
    setImage(null);
  };

  const preventExtraClicks = useCallback((e) => {
    const itemName = e.target.getAttribute('name');
    if (itemName !== 'upload') {
      e.preventDefault();
    }
  }, []);

  return (
    <LabelWrapper>
      <ImageBox
        image={image}
        src={getUrl(image)}
        errorMessage={error && error.message}
        placeholder={placeholder}
      />
      <HiddenInput
        ref={inputRef}
        type="file"
        name={name}
        onChange={handleChange}
        accept={SUPPORTED_FORMATS.join(', ')}
      />
      <DeleteImageButton visible={!!image} type="button" onClick={handleDelete}>
        <StyledDeleteIcon />
      </DeleteImageButton>
      <ButtonsContainer onClick={preventExtraClicks}>
        <TextButton name="upload">
          {label || t('community.selectLogo')}
        </TextButton>
        <TipText>{t('community.selectLogoTip')}</TipText>
      </ButtonsContainer>
    </LabelWrapper>
  );
};

export default ImageInput;
